import _ from 'lodash'
import React from 'react'
import Formatter from '../../Components/Formatter'
import { qf } from '../../Queries/queryFormatter'
import pluralize from 'pluralize'
import { queryFilters } from '../../Queries/queryFilters'
import { capitalCase } from 'change-case'
import cuid from 'cuid'
import {
    canViewProjectDates,
    canViewProjectExpenseBudgets,
    canViewProjectStaffCost,
    canViewProjectFees,
    canViewProjectInvoices,
    canViewProjectExpenses,
    canViewStaffAllocations,
    canViewProjectStaffPay,
    canViewProjectStaffChargeOut,
    canViewCostCentres,
    canViewContacts,
    canViewProjectNotes,
    canViewRevenueTargets,
    canViewPrimaryContact,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'

const getPhaseFilters = (report) => {
    return [
        ...report.filters
            .filter((f) => f.group === 'phase')
            .map((f) => report.columnOptionsById[f.column]?.queryFilters?.(f))
            .flat(),
    ]
}

export const ProjectReportColumns = (report) => ({
    name: {
        id: 'name',
        label: 'Name',
        type: 'text',
        width: 25,
        value: (row, stores) => {
            return stores.row.group
                ? stores.row.cells[stores.row.group].formattedValue
                : row.title
        },
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('name', value),
        component: ({ value, group, stores }) => {
            const { row, table } = stores
            if (row.group === 'totals') return 'Total'
            return (
                <div style={{ paddingLeft: `${1 * row.groupLevel}rem` }}>
                    {row.childRows.length ? (
                        <i
                            className={`fa fa-${`caret-${
                                row.expanded ? 'down' : 'right'
                            }`} fa-fw`}
                            onClick={() => table.toggleRowExpand(row)}
                        />
                    ) : null}
                    {row.rowObject.modelType === 'project' ? value : value}
                </div>
            )
        },
    },
    project: {
        id: 'project',
        label: 'Project',
        type: 'project',
        width: 25,
        value: (row) =>
            row.project || (row.modelType === 'project' ? row : null),
        queryFilters: ({ operator, value, group }) =>
            queryFilters.singleId[operator](
                group === 'project' ? 'id' : 'projectId',
                value
            ),
    },
    jobCode: {
        id: 'jobCode',
        label: 'Job code',
        type: 'text',
        width: 10,
        value: (row) => row.jobNumber,
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('jobNumber', value),
        aggregate: 'none',
    },
    startDate: {
        id: 'startDate',
        label: 'Start date',
        type: 'date',
        width: 10,
        value: (row) => row.startDate,
        aggregate: 'min',
        permissions: (row) =>
            canViewProjectDates(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) =>
            queryFilters.date[operator]('startDate', value),
    },
    endDate: {
        id: 'endDate',
        label: 'End date',
        type: 'date',
        width: 10,
        value: (row) => row.endDate,
        aggregate: 'max',
        permissions: (row) =>
            canViewProjectDates(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) =>
            queryFilters.date[operator]('endDate', value),
    },
    fee: {
        id: 'fee',
        label: 'Fee',
        type: 'currency',
        width: 10,
        value: (row) => row.fee,
        permissions: (row) =>
            canViewProjectFees(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('fee', value)
        },
    },
    expenseBudget: {
        id: 'expenseBudget',
        label: 'Expense Budget',
        type: 'currency',
        width: 10,
        value: (row) => row.expenseBudget,
        permissions: (row) =>
            canViewProjectExpenseBudgets(
                SessionStore.user,
                row?.project || row
            ),
        queryFilters: ({ operator, value }) =>
            queryFilters.number[operator]('expenseBudget', value),
    },
    costCentre: {
        id: 'costCentre',
        label: 'Cost centre',
        type: 'costCentre',
        width: 10,
        value: (row) => row.costCentre,
        permissions: (row) => canViewCostCentres(SessionStore.user),
        queryFilters: ({ operator, value }) =>
            queryFilters.singleId[operator]('costCentreId', value),
    },
    contact: {
        id: 'contact',
        label: 'Contact',
        type: 'contact',
        width: 10,
        value: (row) => row.contact,
        permissions: (row) => canViewContacts(SessionStore.user),
        queryFilters: ({ operator, value }) =>
            queryFilters.singleId[operator]('contactId', value),
    },
    invoiceContact: {
        id: 'invoiceContact',
        label: 'Primary Contact',
        type: 'contact',
        width: 10,
        value: (row) => row.primaryContact,
        permissions: (row) =>
            canViewContacts(SessionStore.user) && canViewPrimaryContact(),
        queryFilters: ({ operator, value }) =>
            queryFilters.singleId[operator]('primaryContactId', value),
    },
    owner: {
        id: 'owner',
        label: 'Project Owner',
        type: 'staffMember',
        width: 15,
        value: (row) => row.owner,
        queryFilters: ({ operator, value }) =>
            queryFilters.singleId[operator]('ownerId', value),
    },
    status: {
        id: 'status',
        label: 'Status',
        type: 'status',
        width: 10,
        value: (row) => {
            return row.status || 'active'
        },
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('status', value),
    },
    remainingFee: {
        id: 'remainingFee',
        label: 'Remaining fee',
        type: 'currency',
        width: 10,
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) =>
            queryFilters.number[operator]('(fee - revenue)', value),
        value: (row) =>
            ProjectReportColumns(report).fee.value(row) -
            ProjectReportColumns(report).revenue.value(row),
    },
    remainingExpenseProjectBudget: {
        id: 'remainingExpenseProjectBudget',
        label: 'Remaining Expense Budget',
        type: 'currency',
        width: 10,
        queryFilters: ({ operator, value }) =>
            queryFilters.number[operator](
                '(expenseBudget - expenses - projectExpenses)',
                value
            ),
        value: (row) =>
            ProjectReportColumns(report).expenseBudget.value(row) -
            ProjectReportColumns(report).expenses.value(row) -
            ProjectReportColumns(report).projectExpenses.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).expenseBudget.permissions(row) &&
            ProjectReportColumns(report).expenses.permissions(row) &&
            ProjectReportColumns(report).projectExpenses.permissions(row),
    },
    latestEvent: {
        id: 'latestEvent',
        label: 'Latest Event',
        type: 'date',
        width: 10,
        value: (row) =>
            row.history.length
                ? _.max(
                      row.history
                          .filter((h) =>
                              row.modelType === 'phase'
                                  ? h.phaseId === row.id
                                  : true
                          )
                          .map((h) => new Date(h.time))
                  )
                : null,
    },
    latestNote: {
        id: 'latestNote',
        label: 'Latest Note',
        type: 'text',
        width: 35,
        value: (row) => {
            const notes = row.notes || []
            return notes.length
                ? _.orderBy(notes, ['date'], ['desc'])[0].description
                : null
        },
        permissions: (row) =>
            canViewProjectNotes(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('latestNoteDescription', value),
    },
    currentPhases: {
        id: 'currentPhases',
        label: 'Current Phases',
        type: 'text',
        width: 10,
        value: (row) => row.currentPhaseTitles,
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('currentPhaseTitles', value),
    },
    activePhases: {
        id: 'activePhases',
        label: 'Active Phases',
        type: 'phase',
        width: 10,
        value: (row) => row.activePhaseTitles,
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('activePhaseTitles', value),
    },
    prospectivePhases: {
        id: 'prospectivePhases',
        label: 'Prospective Phases',
        type: 'phase',
        width: 10,
        value: (row) => row.prospectivePhaseTitles,
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('prospectivePhaseTitles', value),
    },
    staffMembers: {
        id: 'staffMembers',
        label: 'Staff members assigned',
        type: 'staffs',
        width: 10,
        value: (row) => row.budgets.map((b) => b.staff),
        queryFilters: ({ operator, value }) =>
            queryFilters.manyIds[operator]('staffMemberIds', value),
    },
    progress: {
        id: 'progress',
        label: 'Progress',
        type: 'percent',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).revenue.value(row) /
            ProjectReportColumns(report).fee.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? revenue / fee : 0)',
                value
            )
        },
    },
    revenue: {
        id: 'revenue',
        label: 'Revenue',
        type: 'currency',
        width: 10,
        value: (row) => row.revenue,
        permissions: (row) =>
            canViewProjectInvoices(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('revenue', value)
        },
    },
    revenueWithVariations: {
        id: 'revenueWithVariations',
        label: 'Revenue (Variations)',
        type: 'currency',
        width: 10,
        value: (row) => row.revenueWithVariations,
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('revenueWithVariations', value)
        },
    },
    revenueWithVariationsReimbursements: {
        id: 'revenueWithVariationsReimbursements',
        label: 'Revenue (Variations + Reimbursements)',
        type: 'currency',
        width: 10,
        value: (row) => row.revenueWithVariationsReimbursements,
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenueWithVariationsReimbursements',
                value
            )
        },
    },
    recordedHours: {
        id: 'recordedHours',
        label: 'Recorded Hours',
        type: 'number',
        width: 10,
        value: (row) => row.recordedHours,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('recordedHours', value)
        },
    },
    recordedHoursVariation: {
        id: 'recordedHoursVariation',
        label: 'Recorded Hours (Variation)',
        type: 'number',
        width: 10,
        value: (row) => row.recordedHoursVariation,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'recordedHoursVariation',
                value
            )
        },
    },
    hoursBudgeted: {
        id: 'hoursBudgeted',
        label: 'Hours budgeted',
        type: 'number',
        width: 10,
        value: (row) => row.hoursBudget,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('hoursBudget', value)
        },
    },
    hoursAllocated: {
        id: 'hoursAllocated',
        label: 'Hours allocated',
        type: 'number',
        width: 10,
        value: (row) => row.hoursAllocated,
        permissions: (row) =>
            canViewStaffAllocations(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('hoursAllocated', value)
        },
    },
    expenses: {
        id: 'expenses',
        label: 'Expenses (cost)',
        type: 'currency',
        width: 10,
        value: (row) => row._expenses,
        permissions: (row) =>
            canViewProjectStaffCost(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('expenses', value)
        },
    },
    projectExpenses: {
        id: 'projectExpenses',
        label: 'Project Expenses',
        type: 'currency',
        width: 10,
        value: (row) => row.projectExpenses,
        permissions: (row) =>
            canViewProjectExpenses(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('projectExpenses', value)
        },
    },
    expensesVariation: {
        id: 'expensesVariation',
        label: 'Expenses (cost) + Variation Cost',
        type: 'currency',
        width: 10,
        value: (row) => row.expensesVariation,
        permissions: (row) =>
            ProjectReportColumns(report).expenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('expensesVariation', value)
        },
    },
    expensesVariationProject: {
        id: 'expensesVariationProject',
        label: 'Expenses (cost) + Variation Cost + Project Expenses',
        type: 'currency',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).expensesVariation.value(row) +
            ProjectReportColumns(report).projectExpenses.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).expenses.permissions(row) &&
            ProjectReportColumns(report).projectExpenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'expensesVariation + projectExpenses',
                value
            )
        },
    },
    expensesProject: {
        id: 'expensesProject',
        label: 'Expenses (cost) + Project Expenses',
        type: 'currency',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).expenses.value(row) +
            ProjectReportColumns(report).projectExpenses.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).expenses.permissions(row) &&
            ProjectReportColumns(report).projectExpenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'expenses + projectExpenses',
                value
            )
        },
    },
    labourExpense: {
        id: 'labourExpense',
        label: 'Labour expense',
        type: 'currency',
        width: 10,
        value: (row) => row.labourExpense,
        permissions: (row) =>
            canViewProjectStaffPay(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('labourExpense', value)
        },
    },
    chargeOut: {
        id: 'chargeOut',
        label: 'Charge-out',
        type: 'currency',
        width: 10,
        value: (row) => row.chargeOut,
        permissions: (row) =>
            canViewProjectStaffChargeOut(
                SessionStore.user,
                row?.project || row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOut', value)
        },
    },
    chargeOutVariation: {
        id: 'chargeOutVariation',
        label: 'Charge-out (Variations)',
        type: 'currency',
        width: 10,
        value: (row) => row.chargeOutVariation,
        permissions: (row) =>
            ProjectReportColumns(report).chargeOut.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOutVariation', value)
        },
    },
    actualVsBudgetedHours: {
        id: 'actualVsBudgetedHours',
        label: 'Actual / Budgeted Hours',
        type: 'progressBar',
        width: 20,
        value: (row) => {
            return {
                numerator:
                    ProjectReportColumns(report).recordedHours.value(row),
                denominator:
                    ProjectReportColumns(report).hoursBudgeted.value(row),
            }
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(hoursBudget > 0 and hoursBudget != null ? recordedHours / hoursBudget : 0)',
                value
            )
        },
    },
    actualVsBudgetedHoursVariation: {
        id: 'actualVsBudgetedHoursVariation',
        label: 'Actual (Variation) / Budgeted Hours',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator:
                ProjectReportColumns(report).recordedHoursVariation.value(row),
            denominator: ProjectReportColumns(report).hoursBudgeted.value(row),
        }),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(hoursBudget > 0 and hoursBudget != null ? recordedHoursVariation / hoursBudget : 0)',
                value
            )
        },
    },
    actualProjectVsBudgetedExpenses: {
        id: 'actualProjectVsBudgetedExpenses',
        label: 'Actual (Cost + Project) / Budgeted Expenses',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).expensesProject.value(row),
            denominator: ProjectReportColumns(report).expenseBudget.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expensesProject.permissions(row) &&
            ProjectReportColumns(report).expenseBudget.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expenseBudget > 0 and expenseBudget != null ? (expenses + projectExpenses) / expenseBudget : 0)',
                value
            )
        },
    },
    actualVariationProjectVsBudgetedExpenses: {
        id: 'actualVariationProjectVsBudgetedExpenses',
        label: 'Actual (Cost + Project + Variation) / Budgeted Expenses',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator:
                ProjectReportColumns(report).expensesVariationProject.value(
                    row
                ),
            denominator: ProjectReportColumns(report).expenseBudget.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ) && ProjectReportColumns(report).expenseBudget.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expenseBudget > 0 and expenseBudget != null ? (expensesVariation + projectExpenses) / expenseBudget : 0)',
                value
            )
        },
    },
    actualVariationVsBudgetedExpenses: {
        id: 'actualVariationVsBudgetedExpenses',
        label: 'Actual (Cost) / Budgeted Expenses',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator:
                ProjectReportColumns(report).expensesVariation.value(row),
            denominator: ProjectReportColumns(report).expenseBudget.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expensesVariation.permissions(row) &&
            ProjectReportColumns(report).expenseBudget.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expenseBudget > 0 and expenseBudget != null ? expensesVariation / expenseBudget : 0)',
                value
            )
        },
    },
    actualVsBudgetedExpenses: {
        id: 'actualVsBudgetedExpenses',
        label: 'Actual (Cost) / Budgeted Expenses',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).expenses.value(row),
            denominator: ProjectReportColumns(report).expenseBudget.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expenses.permissions(row) &&
            ProjectReportColumns(report).expenseBudget.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expenseBudget > 0 and expenseBudget != null ? expenses / expenseBudget : 0)',
                value
            )
        },
    },
    revenueVsFee: {
        id: 'revenueVsFee',
        label: 'Revenue / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).revenue.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        format: Formatter.currency,
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? revenue / fee : 0)',
                value
            )
        },
    },
    percentRevenueVsFee: {
        id: 'percentRevenueVsFee',
        label: 'Percent Revenue / Fee',
        type: 'percent',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).fee.value(row)
                ? ProjectReportColumns(report).revenue.value(row) /
                  ProjectReportColumns(report).fee.value(row)
                : 0,
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? revenue / fee : 0)',
                value
            )
        },
    },
    costProjectVsFee: {
        id: 'costProjectVsFee',
        label: 'Expenses (Cost + Project) / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).expensesProject.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expensesProject.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? (expenses + projectExpenses) / fee : 0)',
                value
            )
        },
        format: Formatter.currency,
    },
    costVsFee: {
        id: 'costVsFee',
        label: 'Expense (cost) / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).expenses.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).expenses.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? expenses / fee : 0)',
                value
            )
        },
        format: Formatter.currency,
    },
    payVsFee: {
        id: 'payVsFee',
        label: 'Expense (labour) / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).labourExpense.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).labourExpense.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? labourExpense / fee : 0)',
                value
            )
        },
        format: Formatter.currency,
    },
    chargeOutVsFee: {
        id: 'chargeOutVsFee',
        label: 'Charge Out / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator: ProjectReportColumns(report).chargeOut.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).chargeOut.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? chargeOut / fee : 0)',
                value
            )
        },
        format: Formatter.currency,
    },
    chargeOutVariationVsFee: {
        id: 'chargeOutVariationVsFee',
        label: 'Charge Out (Variations) / Fee',
        type: 'progressBar',
        width: 20,
        value: (row) => ({
            numerator:
                ProjectReportColumns(report).chargeOutVariation.value(row),
            denominator: ProjectReportColumns(report).fee.value(row),
        }),
        permissions: (row) =>
            ProjectReportColumns(report).chargeOutVariation.permissions(row) &&
            ProjectReportColumns(report).fee.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(fee > 0 and fee != null ? chargeOutVariation / fee : 0)',
                value
            )
        },
        format: Formatter.currency,
    },
    percentOfTotalHours: {
        id: 'percentOfTotalHours',
        label: 'Percentage of total hours',
        type: 'percent',
        width: 10,
        value: (row, stores) => {
            return (
                ProjectReportColumns(report).recordedHours.value(row) /
                stores.table.totalsRow.cells['recordedHours'].value
            )
        },
    },
    percentOfTotalRevenue: {
        id: 'percentOfTotalRevenue',
        label: 'Percentage of total revenue',
        type: 'percent',
        width: 10,
        value: (row, stores) => {
            return (
                ProjectReportColumns(report).revenue.value(row) /
                stores.table.totalsRow.cells['revenue'].value
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row),
    },
    percentOfTotalExpenses: {
        id: 'percentOfTotalExpenses',
        label: 'Percentage of total expenses (cost)',
        type: 'percent',
        width: 10,
        value: (row, stores) => {
            return (
                ProjectReportColumns(report).expensesProject.value(row) /
                stores.table.totalsRow.cells['expensesProject'].value
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).expensesProject.permissions(row),
    },
    percentOfTotalExpensesLabour: {
        id: 'percentOfTotalExpensesLabour',
        label: 'Percentage of total expenses (labour)',
        type: 'percent',
        width: 10,
        value: (row, stores) => {
            return (
                ProjectReportColumns(report).labourExpense.value(row) /
                stores.table.totalsRow.cells['labourExpense'].value
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).labourExpense.permissions(row),
    },
    profit: {
        id: 'profit',
        label: 'Profit (cost + project expenses)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).revenue.value(row) -
                ProjectReportColumns(report).expensesProject.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row) &&
            ProjectReportColumns(report).expensesProject.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenue - (expenses + projectExpenses)',
                value
            )
        },
    },
    profitCost: {
        id: 'profitCost',
        label: 'Profit (cost)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).revenue.value(row) -
                ProjectReportColumns(report).expenses.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row) &&
            ProjectReportColumns(report).expenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('revenue - expenses', value)
        },
    },
    profitLabour: {
        id: 'profitLabour',
        label: 'Profit (labour)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).revenue.value(row) -
                ProjectReportColumns(report).labourExpense.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row) &&
            ProjectReportColumns(report).labourExpense.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenue - labourExpense',
                value
            )
        },
    },
    profitFee: {
        id: 'profitFee',
        label: 'Profit (fee)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).fee.value(row) -
                ProjectReportColumns(report).expensesProject.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).expensesProject.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'fee - (expenses + projectExpenses)',
                value
            )
        },
    },
    profitFeeVariation: {
        id: 'profitFeeVariation',
        label: 'Profit (fee, variation & project expenses)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).fee.value(row) -
                ProjectReportColumns(report).expensesVariationProject.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'fee - (expensesVariation + projectExpenses)',
                value
            )
        },
    },
    profitMargin: {
        id: 'profitMargin',
        label: 'Profit margin (cost + project expenses)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profit.value(row) /
                ProjectReportColumns(report).revenue.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profit.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(revenue > 0 and revenue != null ? (revenue - (expenses + projectExpenses)) / revenue : 0)',
                value
            )
        },
    },
    profitMarginCost: {
        id: 'profitMarginCost',
        label: 'Profit margin (cost)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profitCost.value(row) /
                ProjectReportColumns(report).revenue.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitCost.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(revenue > 0 and revenue != null ? (revenue - expenses) / revenue : 0)',
                value
            )
        },
    },
    profitMarginLabour: {
        id: 'profitMarginLabour',
        label: 'Profit margin (labour)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profitLabour.value(row) /
                ProjectReportColumns(report).revenue.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitLabour.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(revenue > 0 and revenue != null ? (revenue - labourExpense) / revenue : 0)',
                value
            )
        },
    },
    markup: {
        id: 'markup',
        label: 'Markup (cost + project expenses)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profit.value(row) /
                ProjectReportColumns(report).expensesProject.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profit.permissions(row) &&
            ProjectReportColumns(report).expensesProject.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '((expenses + projectExpenses) > 0 and (expenses + projectExpenses) != null ? (revenue - (expenses + projectExpenses)) / (expenses + projectExpenses) : 0)',
                value
            )
        },
    },
    markupCost: {
        id: 'markupCost',
        label: 'Markup (cost)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profitCost.value(row) /
                ProjectReportColumns(report).expenses.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitCost.permissions(row) &&
            ProjectReportColumns(report).expenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expenses > 0 and expenses != null ? (revenue - expenses) / expenses : 0)',
                value
            )
        },
    },
    markupLabour: {
        id: 'markupLabour',
        label: 'Markup (labour)',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).profitLabour.value(row) /
                ProjectReportColumns(report).labourExpense.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitLabour.permissions(row) &&
            ProjectReportColumns(report).labourExpense.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(labourExpense > 0 and labourExpense != null ? (revenue - labourExpense) / labourExpense : 0)',
                value
            )
        },
    },
    revenuePerHour: {
        id: 'revenuePerHour',
        label: 'Revenue per hour',
        type: 'currency',
        width: 10,
        value: (row) => {
            return ProjectReportColumns(report).recordedHours.value(row)
                ? ProjectReportColumns(report).revenue.value(row) /
                      ProjectReportColumns(report).recordedHours.value(row)
                : 0
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(recordedHours > 0 and recordedHours != null ? revenue / recordedHours : 0)',
                value
            )
        },
    },
    revenueVariationPerHour: {
        id: 'revenueVariationPerHour',
        label: 'Revenue per hour (variations)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return ProjectReportColumns(report).recordedHoursVariation.value(
                row
            )
                ? ProjectReportColumns(report).revenueWithVariations.value(
                      row
                  ) /
                      ProjectReportColumns(report).recordedHoursVariation.value(
                          row
                      )
                : 0
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenueWithVariations.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(recordedHoursVariation > 0 and recordedHoursVariation != null ? revenueWithVariations / recordedHoursVariation : 0)',
                value
            )
        },
    },
    revenueVariationReimbursementPerHour: {
        id: 'revenueVariationReimbursementPerHour',
        label: 'Revenue per hour (variations + reimbursements)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return ProjectReportColumns(report).recordedHoursVariation.value(
                row
            )
                ? ProjectReportColumns(
                      report
                  ).revenueWithVariationsReimbursements.value(row) /
                      ProjectReportColumns(report).recordedHoursVariation.value(
                          row
                      )
                : 0
        },
        permissions: (row) =>
            ProjectReportColumns(
                report
            ).revenueWithVariationsReimbursements.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(recordedHoursVariation > 0 and recordedHoursVariation != null ? revenueWithVariationsReimbursements / recordedHoursVariation : 0)',
                value
            )
        },
    },
    completionDate: {
        id: 'completionDate',
        label: 'Completion date',
        type: 'date',
        width: 10,
        value: (row) => {
            return ProjectReportColumns(report).fee.value(row) &&
                ProjectReportColumns(report).revenue.value(row) /
                    ProjectReportColumns(report).fee.value(row) >=
                    1
                ? ProjectReportColumns(report).latestRevenueDate.value(row)
                : null
        },
        permissions: (row) =>
            ProjectReportColumns(report).fee.permissions(row) &&
            ProjectReportColumns(report).revenue.permissions(row),
        queryFilters: ({ operator, value }) =>
            queryFilters.date[operator](
                'fee > 0 and (revenue / fee) > 1 ? latestInvoiceDate : null',
                value
            ),
    },
    latestInvoicedPhases: {
        id: 'latestInvoicedPhases',
        label: 'Latest Invoiced Phases',
        type: 'text',
        width: 15,
        value: (row) => {
            return row.latestInvoicedPhases
        },
        permissions: (row) =>
            canViewProjectInvoices(SessionStore.sessionUser, row),
        queryFilters: ({ operator, value }) =>
            queryFilters.text[operator]('latestInvoicedPhaseNames', value),
    },
    latestRevenueDate: {
        id: 'latestRevenueDate',
        label: 'Latest Revenue Date',
        type: 'date',
        width: 10,
        value: (row) => {
            return row.latestInvoiceDate
        },
        permissions: (row) =>
            canViewProjectInvoices(SessionStore.sessionUser, row),
    },
    projectedHours: {
        id: 'projectedHours',
        label: 'Projected Hours',
        type: 'number',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).recordedHours.value(row) +
                ProjectReportColumns(report).futureHours.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).futureHours.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'recordedHours + futureHours',
                value
            )
        },
    },
    futureHours: {
        id: 'futureHours',
        label: 'Future Hours',
        type: 'number',
        width: 10,
        value: (row) => {
            return row.futureHours
        },
        permissions: (row) =>
            canViewStaffAllocations(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureHours', value)
        },
    },
    futureLabour: {
        id: 'futureLabour',
        label: 'Future Labour Expense',
        type: 'number',
        width: 10,
        value: (row) => {
            return row.futureLabour
        },
        permissions: (row) =>
            canViewStaffAllocations(SessionStore.user, row?.project || row) &&
            canViewProjectStaffPay(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureLabour', value)
        },
    },
    futureCost: {
        id: 'futureCost',
        label: 'Future Expense',
        type: 'number',
        width: 10,
        value: (row) => {
            return row.futureCost
        },
        permissions: (row) =>
            canViewStaffAllocations(SessionStore.user, row?.project || row) &&
            canViewProjectStaffCost(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureCost', value)
        },
    },
    futureChargeOut: {
        id: 'futureChargeOut',
        label: 'Future Charge Out',
        type: 'number',
        width: 10,
        value: (row) => {
            return row.futureChargeOut
        },
        permissions: (row) =>
            canViewStaffAllocations(SessionStore.user, row?.project || row) &&
            canViewProjectStaffChargeOut(
                SessionStore.user,
                row?.project || row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureChargeOut', value)
        },
    },
    futureRevenue: {
        id: 'futureRevenue',
        label: 'Future Revenue',
        type: 'currency',
        width: 10,
        value: (row) => {
            return row.futureRevenue
        },
        permissions: (row) =>
            canViewRevenueTargets(SessionStore.user, row?.project || row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureRevenue', value)
        },
    },
    projectedRevenue: {
        id: 'projectedRevenue',
        label: 'Projected Revenue',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).revenue.value(row) +
                ProjectReportColumns(report).futureRevenue.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenue.permissions(row) &&
            ProjectReportColumns(report).futureRevenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenue + futureRevenue',
                value
            )
        },
    },
    projectedExpense: {
        id: 'projectedExpense',
        label: 'Projected Expense (Cost)',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).futureCost.value(row) +
                ProjectReportColumns(report).expenses.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).futureCost.permissions(row) &&
            ProjectReportColumns(report).expenses.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('futureCost + expenses', value)
        },
    },
    projectedLabourExpense: {
        id: 'projectedLabourExpense',
        label: 'Projected Labour Expense',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).futureLabour.value(row) +
                ProjectReportColumns(report).labourExpense.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).futureLabour.permissions(row) &&
            ProjectReportColumns(report).labourExpense.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'futureLabour + labourExpense',
                value
            )
        },
    },
    projectedChargeOut: {
        id: 'projectedChargeOut',
        label: 'Projected Charge Out',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).futureChargeOut.value(row) +
                ProjectReportColumns(report).chargeOut.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).futureChargeOut.permissions(row) &&
            ProjectReportColumns(report).chargeOut.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'futureChargeOut + chargeOut',
                value
            )
        },
    },
    projectedProfit: {
        id: 'projectedProfit',
        label: 'Projected Profit',
        type: 'currency',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).projectedRevenue.value(row) -
                ProjectReportColumns(report).projectedExpense.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).projectedRevenue.permissions(row) &&
            ProjectReportColumns(report).projectedExpense.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(revenue + futureRevenue) - (futureCost + expenses)',
                value
            )
        },
    },
    projectedProfitMargin: {
        id: 'projectedProfitMargin',
        label: 'Projected Profit Margin',
        type: 'percent',
        width: 10,
        value: (row) => {
            return (
                ProjectReportColumns(report).projectedProfit.value(row) /
                ProjectReportColumns(report).projectedRevenue.value(row)
            )
        },
        permissions: (row) =>
            ProjectReportColumns(report).projectedProfit.permissions(row) &&
            ProjectReportColumns(report).projectedRevenue.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(revenue + futureRevenue) > 0 ? ((revenue + futureRevenue) - (futureCost + expenses)) / (revenue + futureRevenue) : 0',
                value
            )
        },
    },
    profitVariationProject: {
        id: 'profitVariationProject',
        label: 'Profit (Cost + Variation + Project)',
        type: 'currency',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).revenueWithVariations.value(row) -
            ProjectReportColumns(report).expensesVariationProject.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).revenueWithVariations.permissions(
                row
            ) &&
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenueWithVariations - (expensesVariation + projectExpenses)',
                value
            )
        },
    },
    profitMarginVariationProject: {
        id: 'profitMarginVariationProject',
        label: 'Profit Margin (Cost + Variation + Project)',
        type: 'percent',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).profitVariationProject.value(row) /
            ProjectReportColumns(report).revenueWithVariations.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).profitVariationProject.permissions(
                row
            ) &&
            ProjectReportColumns(report).revenueWithVariations.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenueWithVariations > 0 ? (revenueWithVariations - (expensesVariation + projectExpenses)) / revenueWithVariations : 0',
                value
            )
        },
    },
    markupVariationProject: {
        id: 'markupVariationProject',
        label: 'Markup (Cost + Variation + Project)',
        type: 'percent',
        width: 10,
        value: (row) =>
            ProjectReportColumns(report).profitVariationProject.value(row) /
            ProjectReportColumns(report).expensesVariationProject.value(row),
        permissions: (row) =>
            ProjectReportColumns(report).profitVariationProject.permissions(
                row
            ) &&
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expensesVariation + projectExpenses) > 0 ? (revenueWithVariations - (expensesVariation + projectExpenses)) / (expensesVariation + projectExpenses) : 0',
                value
            )
        },
    },
    expensesVsRevenueVariationProject: {
        id: 'expensesVsRevenueVariationProject',
        label: 'Expenses / Revenue (Cost + Variation + Project)',
        type: 'progressBar',
        width: 20,
        value: (row) => {
            return {
                numerator:
                    ProjectReportColumns(report).expensesVariationProject.value(
                        row
                    ),
                denominator:
                    ProjectReportColumns(report).revenueWithVariations.value(
                        row
                    ),
            }
        },
        permissions: (row) =>
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ) &&
            ProjectReportColumns(report).revenueWithVariations.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenueWithVariations > 0 ? (expensesVariation + projectExpenses) / revenueWithVariations : 0',
                value
            )
        },
    },
    revenueVsExpensesVariationProject: {
        id: 'revenueVsExpensesVariationProject',
        label: 'Revenue / Expenses (Cost + Variation + Project)',
        type: 'progressBar',
        width: 20,
        value: (row) => {
            return {
                numerator:
                    ProjectReportColumns(report).revenueWithVariations.value(
                        row
                    ),
                denominator:
                    ProjectReportColumns(report).expensesVariationProject.value(
                        row
                    ),
            }
        },
        permissions: (row) =>
            ProjectReportColumns(report).revenueWithVariations.permissions(
                row
            ) &&
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expensesVariation + projectExpenses) > 0 ? revenueWithVariations / (expensesVariation + projectExpenses) : 0',
                value
            )
        },
    },
    profitVsRevenueVariationProject: {
        id: 'profitVsRevenueVariationProject',
        label: 'Profit / Revenue (Cost + Variation + Project)',
        type: 'progressBar',
        width: 20,
        value: (row) => {
            return {
                numerator:
                    ProjectReportColumns(report).profitVariationProject.value(
                        row
                    ),
                denominator:
                    ProjectReportColumns(report).revenueWithVariations.value(
                        row
                    ),
            }
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitVariationProject.permissions(
                row
            ) &&
            ProjectReportColumns(report).revenueWithVariations.permissions(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'revenueWithVariations > 0 ? (revenueWithVariations - (expensesVariation + projectExpenses)) / revenueWithVariations : 0',
                value
            )
        },
    },
    profitVsExpensesVariationProject: {
        id: 'profitVsExpensesVariationProject',
        label: 'Profit / Expenses (Cost + Variation + Project)',
        type: 'progressBar',
        width: 20,
        value: (row) => {
            return {
                numerator:
                    ProjectReportColumns(report).profitVariationProject.value(
                        row
                    ),
                denominator:
                    ProjectReportColumns(report).expensesVariationProject.value(
                        row
                    ),
            }
        },
        permissions: (row) =>
            ProjectReportColumns(report).profitVariationProject.permissions(
                row
            ) &&
            ProjectReportColumns(report).expensesVariationProject.permissions(
                row
            ),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                '(expensesVariation + projectExpenses) > 0 ? (revenueWithVariations - (expensesVariation + projectExpenses)) / (expensesVariation + projectExpenses) : 0',
                value
            )
        },
    },
})
